// The browser client of the selected saved App's SOURCES (APP's source authoring), for exactly one editor
// selection, on the frame's SESSION-BOUND fetch:
//   GET  /api/v1/company/artifacts/:artifactId/app/sources?versionId&revisionId&purpose=editor
//        -> {saved, manifest}: the named saved App and what it declares (sources, tools, refresh jobs)
//   POST …/app/sources/prepare  {selection, operationId, locale, sourceId, sourceName, toolId, toolName, toolRef,
//        argumentTemplate, intent?}  -> a finish candidate reference
//   POST …/app/refresh/prepare  {selection, operationId, locale, sourceId, schedule:{expression,timeZone}}
//        -> a finish candidate reference
// The two POSTs WRITE (they prepare a candidate version), so each command keeps ONE operation id until the server
// answers it. The reads are plain. Nothing here sends a company, actor or logical App id; the server resolves them.
export type SourcesSelection = {
  artifactId: string;
  versionId: string;
  revisionId: string;
  purpose: 'editor';
};
export type DeclaredSource = { id: string; name: string };
export type DeclaredTool = {
  id: string;
  name: string;
  sourceId: string | null;
  /** What a renew re-sends for exactly this retained tool (the manifest's own, never composed here). */
  toolRef: string | null;
  argumentTemplate: Record<string, unknown>;
};
export type DeclaredRefreshJob = {
  id: string;
  name: string;
  /** The source this refresh reads, when the declaration names one (a trusted refresh target). */
  sourceId: string | null;
  schedule: { expression: string; timeZone: string };
};
export type SourcesRead = {
  sources: DeclaredSource[];
  tools: DeclaredTool[];
  jobs: DeclaredRefreshJob[];
};
export type CandidateReference = { artifactId: string; candidateRef: string; operationId: string };

export class AppSourcesUnavailable extends Error {
  constructor(readonly status: number | null) {
    super('app_sources_unavailable');
    this.name = 'AppSourcesUnavailable';
  }
}

const obj = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
const str = (value: unknown) => (typeof value === 'string' && value.length > 0 ? value : null);

export function parseSourcesRead(value: unknown): SourcesRead | null {
  const manifest = obj(obj(value)?.manifest);
  if (!manifest) return null;
  const list = (key: string) => (manifest[key] === undefined ? [] : manifest[key]);
  const sourcesIn = list('sources');
  const toolsIn = list('tools');
  const jobsIn = list('jobs');
  if (!Array.isArray(sourcesIn) || !Array.isArray(toolsIn) || !Array.isArray(jobsIn)) return null;
  const sources: DeclaredSource[] = [];
  for (const entry of sourcesIn) {
    const raw = obj(entry);
    if (!raw || !str(raw.id) || !str(raw.name)) return null;
    sources.push({ id: String(raw.id), name: String(raw.name) });
  }
  const tools: DeclaredTool[] = [];
  for (const entry of toolsIn) {
    const raw = obj(entry);
    if (!raw || !str(raw.id) || !str(raw.name)) return null;
    const template = raw.argumentTemplate;
    tools.push({
      id: String(raw.id),
      name: String(raw.name),
      sourceId: str(raw.sourceId),
      toolRef: str(raw.toolRef),
      argumentTemplate: obj(template) ?? {},
    });
  }
  const jobs: DeclaredRefreshJob[] = [];
  for (const entry of jobsIn) {
    const raw = obj(entry);
    const schedule = obj(raw?.schedule);
    // a job without a schedule is not a refresh plan (a function job run on demand): not listed here
    if (!raw || !str(raw.id) || !str(raw.name)) return null;
    if (!schedule) continue;
    if (!str(schedule.expression) || !str(schedule.timeZone)) return null;
    const target = obj(raw.target);
    jobs.push({
      id: String(raw.id),
      name: String(raw.name),
      sourceId: str(target?.sourceId),
      schedule: { expression: String(schedule.expression), timeZone: String(schedule.timeZone) },
    });
  }
  return { sources, tools, jobs };
}

export function parseCandidate(value: unknown): CandidateReference | null {
  const raw = obj(value);
  return raw && str(raw.artifactId) && str(raw.candidateRef) && str(raw.operationId)
    ? {
        artifactId: String(raw.artifactId),
        candidateRef: String(raw.candidateRef),
        operationId: String(raw.operationId),
      }
    : null;
}

const base = (artifactId: string) =>
  `/api/v1/company/artifacts/${encodeURIComponent(artifactId)}/app`;

/** The refresh rollout's answer: ready only after the physical rollout; `saved` is the same C9 version. */
export type RefreshReady = {
  status: 'ready';
  saved: { artifactId: string; versionId: string; revisionId: string };
};
export type RefreshCompletion = { versionId: string; revisionId: string; operationId: string };

export function parseRefreshReady(value: unknown): RefreshReady | null {
  const raw = obj(value);
  const saved = obj(raw?.saved);
  return raw?.status === 'ready' &&
    saved &&
    str(saved.artifactId) &&
    str(saved.versionId) &&
    str(saved.revisionId)
    ? {
        status: 'ready',
        saved: {
          artifactId: String(saved.artifactId),
          versionId: String(saved.versionId),
          revisionId: String(saved.revisionId),
        },
      }
    : null;
}

/** A bounded read of one declared source/tool for the SAME saved selection (APP's native preview). */
export type PreviewResult =
  | { ok: true; data: unknown }
  | { ok: false; code: string; retryable: boolean };

export function parsePreview(
  value: unknown,
  expected: {
    artifactId: string;
    versionId: string;
    revisionId: string;
    sourceId: string;
    toolId: string;
  },
): PreviewResult | null {
  const raw = obj(value);
  if (raw?.ok === false) {
    const error = obj(raw.error);
    return str(error?.code)
      ? { ok: false, code: String(error!.code), retryable: error!.retryable === true }
      : null;
  }
  const saved = obj(raw?.saved);
  // the answer must be for exactly the selected saved version and the asked source/tool, or it is not shown
  if (
    raw?.ok !== true ||
    !saved ||
    saved.artifactId !== expected.artifactId ||
    saved.versionId !== expected.versionId ||
    saved.revisionId !== expected.revisionId ||
    raw.sourceId !== expected.sourceId ||
    raw.toolId !== expected.toolId ||
    !('data' in raw)
  )
    return null;
  return { ok: true, data: raw.data };
}

export type AppSourcesApi = {
  /** POST app/sources/preview {artifactId,versionId,sourceId,toolId}: the declared tool's current data, bounded. */
  preview(
    input: { sourceId: string; toolId: string },
    options?: { signal?: AbortSignal },
  ): Promise<PreviewResult>;
  /**
   * POST app/refresh/complete after the committed Finish and its exact read-back, with the ORIGINAL refresh
   * prepare key and saved tuple. Same-key retries recover that rollout. Resolves ready only after it finishes.
   */
  completeRefresh(input: RefreshCompletion): Promise<RefreshReady>;
  /** GET app/refresh/result: READ-ONLY recovery of that original completion: ready, or null while unknown. */
  refreshResult(input: RefreshCompletion): Promise<RefreshReady | null>;
  read(): Promise<SourcesRead>;
  prepareRefresh(input: {
    operationId: string;
    locale: string;
    sourceId: string;
    schedule: { expression: string; timeZone: string };
  }): Promise<CandidateReference>;
  prepareSource(input: {
    operationId: string;
    locale: string;
    sourceId: string;
    sourceName: string;
    toolId: string;
    toolName: string;
    toolRef: string;
    argumentTemplate: Record<string, unknown>;
    intent?: 'append' | 'renew';
  }): Promise<CandidateReference>;
};

/** `send` is the frame's SESSION-BOUND fetch (a replaced session ends every call through it). */
export function createAppSourcesApi(
  selection: SourcesSelection,
  send: typeof fetch,
): AppSourcesApi {
  const { artifactId, versionId, revisionId, purpose } = selection;
  const post = async (path: string, body: Record<string, unknown>) => {
    const response = await send(`${base(artifactId)}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ artifactId, versionId, revisionId, purpose, ...body }),
    });
    if (response.status !== 200) throw new AppSourcesUnavailable(response.status);
    const candidate = parseCandidate(await response.json().catch(() => null));
    if (!candidate) throw new AppSourcesUnavailable(null);
    return candidate;
  };
  return {
    async read() {
      const query = new URLSearchParams({ versionId, revisionId, purpose });
      const response = await send(`${base(artifactId)}/sources?${query}`, {
        credentials: 'include',
      });
      if (response.status !== 200) throw new AppSourcesUnavailable(response.status);
      const read = parseSourcesRead(await response.json().catch(() => null));
      if (!read) throw new AppSourcesUnavailable(null);
      return read;
    },
    async completeRefresh(input) {
      const ready = parseRefreshReady(
        await (async () => {
          const response = await send(`${base(artifactId)}/refresh/complete`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              artifactId,
              versionId: input.versionId,
              revisionId: input.revisionId,
              purpose,
              operationId: input.operationId,
            }),
          });
          if (response.status !== 200) throw new AppSourcesUnavailable(response.status);
          return response.json().catch(() => null);
        })(),
      );
      if (!ready) throw new AppSourcesUnavailable(null);
      return ready;
    },
    async refreshResult(input) {
      const query = new URLSearchParams({
        versionId: input.versionId,
        revisionId: input.revisionId,
        purpose,
        operationId: input.operationId,
      });
      const response = await send(`${base(artifactId)}/refresh/result?${query}`, {
        credentials: 'include',
      });
      if (response.status !== 200) throw new AppSourcesUnavailable(response.status);
      const body = await response.json().catch(() => undefined);
      if (body === null) return null;
      const ready = parseRefreshReady(body);
      if (!ready) throw new AppSourcesUnavailable(null);
      return ready;
    },
    async preview(input, options = {}) {
      const response = await send(`${base(artifactId)}/sources/preview`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        signal: options.signal,
        body: JSON.stringify({
          artifactId,
          versionId,
          sourceId: input.sourceId,
          toolId: input.toolId,
        }),
      });
      if (response.status !== 200) throw new AppSourcesUnavailable(response.status);
      const result = parsePreview(await response.json().catch(() => null), {
        artifactId,
        versionId,
        revisionId,
        ...input,
      });
      if (!result) throw new AppSourcesUnavailable(null);
      return result;
    },
    prepareRefresh: (input) => post('/refresh/prepare', input),
    prepareSource: (input) => post('/sources/prepare', input),
  };
}
